<template>
  <div v-if="show" class="coach-management-modal">
    <div class="modal-backdrop" @click="close"></div>
    <div class="modal-content">
      <div class="modal-header">
        <h3>Manage Coaches - {{ school.name }}</h3>
        <button @click="close" class="close-button">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <!-- Current Coaches Section -->
        <div class="current-coaches-section">
          <h4>Current Coaches</h4>
          <div class="coaches-list">
            <!-- Creator -->
            <div class="coach-item creator">
              <img 
                :src="school.creator_profile?.avatar || defaultAvatar" 
                :alt="school.creator_profile?.user?.username" 
                class="coach-avatar"
              />
              <div class="coach-info">
                <span class="coach-name">
                  {{ school.creator_profile?.first_name }} {{ school.creator_profile?.last_name }}
                  <img
                    v-if="school.creator_profile?.verified"
                    src="@/assets/verified.png"
                    alt="Verified"
                    class="verified-badge"
                    title="Verified Skier"
                  />
                  <router-link
                    v-if="school.creator_profile?.ski_schools && school.creator_profile.ski_schools.length > 0"
                    :to="`/ski-school/${school.creator_profile.ski_schools[0].slug}`"
                    class="ski-school-link"
                  >
                    <img
                      :src="school.creator_profile.ski_schools[0].logo"
                      alt="Ski School"
                      class="ski-school-badge"
                      :title="`${school.creator_profile.ski_schools[0].name} - ${school.creator_profile.ski_schools[0].role}`"
                    />
                  </router-link>
                  <span v-if="school.creator_profile?.verified" class="pro-badge"></span>
                </span>
                <span class="coach-username">@{{ school.creator_profile?.username }}</span>
                <span class="coach-role creator-role">Creator</span>
              </div>
            </div>

            <!-- Other Coaches -->
            <div 
              v-for="coach in school.coaches_detail" 
              :key="coach.id"
              class="coach-item"
            >
              <img 
                :src="coach.avatar || defaultAvatar" 
                :alt="coach.user?.username" 
                class="coach-avatar"
              />
              <div class="coach-info">
                <span class="coach-name">
                  {{ coach.first_name }} {{ coach.last_name }}
                  <img
                    v-if="coach.verified"
                    src="@/assets/verified.png"
                    alt="Verified"
                    class="verified-badge"
                    title="Verified Skier"
                  />
                  <router-link
                    v-if="coach.ski_schools && coach.ski_schools.length > 0"
                    :to="`/ski-school/${coach.ski_schools[0].slug}`"
                    class="ski-school-link"
                  >
                    <img
                      :src="coach.ski_schools[0].logo"
                      alt="Ski School"
                      class="ski-school-badge"
                      :title="`${coach.ski_schools[0].name} - ${coach.ski_schools[0].role}`"
                    />
                  </router-link>
                  <span v-if="coach.verified" class="pro-badge"></span>
                </span>
                <span class="coach-username">@{{ coach.username }}</span>
                <span class="coach-role">Coach</span>
              </div>
              <button 
                v-if="isCreator" 
                @click="removeCoach(coach)"
                class="remove-coach-button"
                title="Remove coach"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Add New Coach Section -->
        <div class="add-coach-section">
          <h4>Add New Coach</h4>
          <form @submit.prevent="addCoach" class="add-coach-form">
            <div class="form-group">
              <label for="coach-username">Username:</label>
              <input 
                type="text" 
                id="coach-username"
                v-model="newCoachInput" 
                placeholder="Enter username (e.g., @johndoe)"
                class="form-input"
                required
              />
            </div>
            <button type="submit" class="add-coach-button" :disabled="adding">
              <i class="fas fa-plus"></i>
              {{ adding ? 'Adding...' : 'Add Coach' }}
            </button>
          </form>
        </div>

        <!-- Info Section -->
        <div class="info-section">
          <div class="info-item">
            <i class="fas fa-info-circle"></i>
            <span>Only the creator can add or remove coaches</span>
          </div>
          <div class="info-item">
            <span>Coaches can edit school information.</span>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button @click="close" class="cancel-button">
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from '@/config';
import axios from 'axios';

export default {
  name: 'SkiCoachManagement',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    school: {
      type: Object,
      required: true
    },
    userId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      adding: false,
      removing: false,
      newCoachInput: '',
      defaultAvatar: "https://6buoys-bucket.s3.amazonaws.com/avatar.png"
    }
  },
  computed: {
    isCreator() {
      return this.school.creator === this.userId;
    }
  },
  methods: {
    close() {
      this.newCoachInput = '';
      this.$emit('close');
    },

    async addCoach() {
      if (!this.newCoachInput.trim()) return;

      this.adding = true;
      try {
        const token = localStorage.getItem("access_token");
        
        // Clean username (remove @ if present)
        const username = this.newCoachInput.trim().replace('@', '');
        
        // First, get the profile by username to get the profile ID
        const profileResponse = await axios.get(
          `${API_BASE_URL}/api/p/${username}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        const profile = profileResponse.data;
        if (!profile || !profile.id) {
          alert('User not found. Please check the username and try again.');
          return;
        }
        
        // Now add the coach using the profile ID
        await axios.post(
          `${API_BASE_URL}/profiles/ski-schools/${this.school.slug}/add-coach/`,
          {
            profile_id: profile.id
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        // Fetch the updated school data to get the complete coaches list
        const updatedSchoolResponse = await axios.get(
          `${API_BASE_URL}/profiles/ski-schools/${this.school.slug}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        // Emit event to update the school data with the complete updated school
        this.$emit('coach-added', updatedSchoolResponse.data);
        
        this.newCoachInput = '';
        alert('Coach added successfully!');
      } catch (error) {
        console.error('Error adding coach:', error);
        
        if (error.response?.status === 404) {
          alert('User not found. Please check the username and try again.');
        } else if (error.response?.data) {
          const errorMessage = typeof error.response.data === 'string' 
            ? error.response.data 
            : Object.values(error.response.data).flat().join('\n');
          alert(`Error adding coach:\n${errorMessage}`);
        } else {
          alert('Error adding coach. Please try again.');
        }
      } finally {
        this.adding = false;
      }
    },

    async removeCoach(coach) {
      if (!this.isCreator) {
        alert('Only the creator can remove coaches.');
        return;
      }

      if (!confirm(`Are you sure you want to remove ${coach.first_name} ${coach.last_name} as a coach?`)) {
        return;
      }

      this.removing = true;
      try {
        const token = localStorage.getItem("access_token");
        
        await axios.delete(
          `${API_BASE_URL}/profiles/ski-schools/${this.school.slug}/remove-coach/${coach.id}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        // Fetch the updated school data to get the complete updated coaches list
        const updatedSchoolResponse = await axios.get(
          `${API_BASE_URL}/profiles/ski-schools/${this.school.slug}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        // Emit event to update the school data with the complete updated school
        this.$emit('coach-removed', updatedSchoolResponse.data);
        
        alert('Coach removed successfully!');
      } catch (error) {
        console.error('Error removing coach:', error);
        
        if (error.response?.data) {
          const errorMessage = typeof error.response.data === 'string' 
            ? error.response.data 
            : Object.values(error.response.data).flat().join('\n');
          alert(`Error removing coach:\n${errorMessage}`);
        } else {
          alert('Error removing coach. Please try again.');
        }
      } finally {
        this.removing = false;
      }
    }
  }
}
</script>

<style scoped>
.coach-management-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-content {
  position: relative;
  background-color: #192734;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #2a3f54;
}

.modal-header h3 {
  color: #1da1f2;
  margin: 0;
  font-size: 1.5em;
}

.close-button {
  background: none;
  border: none;
  color: #8899a6;
  font-size: 1.2em;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.close-button:hover {
  background-color: #2a3f54;
  color: #d9d9d9;
}

.modal-body {
  padding: 20px;
}

.current-coaches-section,
.add-coach-section {
  margin-bottom: 30px;
}

.current-coaches-section h4,
.add-coach-section h4 {
  color: #d9d9d9;
  margin-bottom: 15px;
  font-size: 1.2em;
  border-bottom: 1px solid #2a3f54;
  padding-bottom: 8px;
}

.coaches-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.coach-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background-color: #15202b;;
  border-radius: 8px;
  position: relative;
  text-align: left;
}

.coach-item.creator {
  border: 1px solid #1da1f2;
  text-align: left;
}

.coach-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
}

.coach-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.coach-name {
  color: #d9d9d9;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.verified-badge {
  width: 16px;
  height: 16px;
}

.ski-school-badge {
  width: 16px;
  height: 16px;
  border-radius: 2px;
  object-fit: cover;
}

.ski-school-link {
  text-decoration: none;
  color: inherit;
}

.coach-username {
  color: #8899a6;
  font-size: 0.9em;
}

.coach-role {
  color: #1da1f2;
  font-size: 0.8em;
  font-weight: 500;
}

.creator-role {
  color: #ffd700;
}

.remove-coach-button {
  background-color: #e74c3c;
  color: #ffffff;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.remove-coach-button:hover {
  background-color: #c0392b;
  transform: scale(1.1);
}

.add-coach-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  color: #d9d9d9;
  font-weight: 500;
}

.form-input {
  padding: 12px;
  border: 1px solid #38444d;
  border-radius: 8px;
  background-color: #15202b;
  color: #d9d9d9;
  font-size: 16px;
  transition: border-color 0.3s ease;
}

.form-input:focus {
  outline: none;
  border-color: #1da1f2;
}

.add-coach-button {
  background-color: #1da1f2;
  color: #ffffff;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.add-coach-button:hover:not(:disabled) {
  background-color: #1a91da;
}

.add-coach-button:disabled {
  background-color: #2a3f54;
  cursor: not-allowed;
}

.info-section {
  background-color: #15202b;;
  padding: 15px;
  border-radius: 8px;
  margin-top: 20px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #8899a6;
  font-size: 0.9em;
  margin-bottom: 8px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-item i {
  color: #1da1f2;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid #2a3f54;
  text-align: right;
}

.cancel-button {
  background-color: transparent;
  color: #8899a6;
  border: 1px solid #2a3f54;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cancel-button:hover {
  background-color: #2a3f54;
  color: #d9d9d9;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    margin: 10px;
  }
  
  .coach-item {
    flex-direction: column;
    text-align: center;
  }
  
  .remove-coach-button {
    position: absolute;
    top: 8px;
    right: 8px;
  }
}
</style> 